/**
 * Everything the van app knows, and the arithmetic it does on the road.
 *
 * The phone prices a line exactly as the server will (qty x price to the
 * halala, less discount, VAT on the net, half-up), so the total printed
 * in the shop is the total the books post. It numbers each document from
 * the van's own series, and builds the ZATCA Phase 1 QR itself: seller,
 * VAT number, the moment, the total and the VAT need no server.
 */
import QRCode from 'qrcode'
import { addMoney, mulMoney, subMoney } from '../vouchers/money'
import { lineAmount } from '../inventory/qty'
import * as db from './db'

// --- the morning download ----------------------------------------------------

export interface VanUnit { unit: string; factor: string; barcode?: string | null
                           sale_price?: string | null }
export interface VanItem {
  id: number; uid: string; code: string; name_en: string; name_ar?: string | null
  base_unit: string; barcode?: string | null; vat_rate: string
  sale_price?: string | null; min_price?: string | null; units: VanUnit[]
  /** Van stock in base units at the download. */
  stock: string
}
export interface VanBill { ref: string; date: string; due?: string | null; outstanding: string }
export interface VanCustomer {
  code: string; name_en: string; name_ar?: string | null; vat_number?: string | null
  cr_number?: string | null; address?: string | null; city?: string | null
  phone?: string | null; credit_days?: number | null
  /** Signed: Dr +, Cr -. */
  balance: string
  bills: VanBill[]
}
export type DocKind = 'SALE' | 'RETURN' | 'RECEIPT'
export interface Bootstrap {
  server_time: string
  fin_year: number
  max_offline_days: number
  device: { id: number; name: string }
  van: { id: number; code: string; name_en: string; name_ar?: string | null
         salesman: string; cash_code?: string | null; cash_name?: string | null }
  company: { name_en: string; name_ar?: string | null; legal_name?: string | null
             vat_number?: string | null; cr_number?: string | null
             address_en?: string | null; address_ar?: string | null; phone?: string | null
             allow_negative_stock: boolean }
  may_override_price: boolean
  series: Record<DocKind, { prefix: string; next: number }>
  items: VanItem[]
  customers: VanCustomer[]
}

// --- what the road produces --------------------------------------------------

export interface DocLine { item_code: string; unit: string; qty: string; price: string
                           discount?: string }
export interface VanDoc {
  uid: string
  kind: DocKind
  doc_no: string
  doc_date: string
  /** UTC, to the second, with a Z: what the QR carries. */
  issued_at: string
  customer_code?: string | null
  customer_name?: string | null
  customer_vat?: string | null
  /** Paid now (into the van's cash) rather than on credit. */
  paid?: boolean
  lines?: DocLine[]
  amount?: string                 // a collection
  subtotal?: string
  vat_amount?: string
  total?: string
  notes?: string | null
  /** Local only. */
  sync?: { status: 'QUEUED' | 'POSTED' | 'DUPLICATE' | 'FAILED'; warnings?: string[]
           error?: string | null; at?: string }
}

export interface Device {
  token: string
  bootstrap: Bootstrap
  /** When the last download came in (ISO): the offline clock starts here. */
  downloaded_at: string
  /** Next number per series, never behind the server's. */
  next: Record<DocKind, number>
}

const K = { device: 'device', docs: 'docs' }

export async function loadDevice(): Promise<Device | undefined> { return db.get<Device>(K.device) }
export async function saveDevice(d: Device): Promise<void> { await db.set(K.device, d) }
export async function loadDocs(): Promise<VanDoc[]> { return (await db.get<VanDoc[]>(K.docs)) ?? [] }
export async function saveDocs(d: VanDoc[]): Promise<void> { await db.set(K.docs, d) }

/** A fresh download: numbers never go backwards, the stock and customers do
 *  as the books say -- less what this phone sold since that the books
 *  have not seen yet. */
export function applyBootstrap(prev: Device | undefined, token: string, b: Bootstrap,
                               unsynced: VanDoc[] = []): Device {
  const next = { ...((prev?.next ?? {}) as Record<DocKind, number>) }
  for (const k of ['SALE', 'RETURN', 'RECEIPT'] as DocKind[]) {
    next[k] = Math.max(next[k] ?? 0, b.series[k].next)
  }
  const fresh: Device = { token, bootstrap: b, downloaded_at: new Date().toISOString(), next }
  return unsynced.length ? replay(fresh, unsynced) : fresh
}

/** The van's stock and the customers' balances after these documents. */
export function replay(d: Device, docs: VanDoc[]): Device {
  const b = structuredClone(d.bootstrap)
  for (const doc of docs) applyLocally(b, doc)
  return { ...d, bootstrap: b }
}

function applyLocally(b: Bootstrap, doc: VanDoc) {
  const sign = doc.kind === 'SALE' ? -1 : doc.kind === 'RETURN' ? 1 : 0
  for (const l of doc.lines ?? []) {
    const it = b.items.find((i) => i.code === l.item_code)
    if (!it) continue
    const base = Number(l.qty) * factorOf(it, l.unit)
    it.stock = String(+(Number(it.stock) + sign * base).toFixed(3))
  }
  const c = doc.customer_code ? b.customers.find((x) => x.code === doc.customer_code) : null
  if (!c) return
  if (doc.kind === 'SALE' && !doc.paid) c.balance = addMoney(c.balance, doc.total ?? '0')
  if (doc.kind === 'RETURN' && !doc.paid) c.balance = subMoney(c.balance, doc.total ?? '0')
  if (doc.kind === 'RECEIPT') c.balance = subMoney(c.balance, doc.amount ?? '0')
}

// --- pricing -----------------------------------------------------------------

export function factorOf(it: VanItem, unit: string): number {
  if (!unit || unit === it.base_unit) return 1
  return Number(it.units.find((u) => u.unit === unit)?.factor ?? 1)
}

export function unitsOf(it: VanItem): string[] {
  return [it.base_unit, ...it.units.map((u) => u.unit)]
}

/** The biggest pack: what a van sells most of. */
export function defaultUnit(it: VanItem): string {
  if (!it.units.length) return it.base_unit
  return [...it.units].sort((a, b) => Number(b.factor) - Number(a.factor))[0].unit
}

export function listPrice(it: VanItem, unit: string): string {
  if (unit !== it.base_unit) {
    const pack = it.units.find((u) => u.unit === unit)
    if (pack?.sale_price) return Number(pack.sale_price).toFixed(2)
  }
  if (!it.sale_price) return ''
  return lineAmount(String(factorOf(it, unit)), it.sale_price)
}

export function minimumFor(it: VanItem, unit: string, qty: string): string | null {
  if (!it.min_price || !qty) return null
  return lineAmount(qty, String(Number(it.min_price) * factorOf(it, unit)))
}

export function priceLine(it: VanItem, l: Pick<DocLine, 'qty' | 'price' | 'discount'>) {
  const gross = l.qty && l.price ? lineAmount(l.qty, l.price) : '0.00'
  const net = subMoney(gross, l.discount || '0')
  const vat = mulMoney(net, String(Number(it.vat_rate) / 100))
  return { gross, net, vat, total: addMoney(net, vat) }
}

export function totals(items: VanItem[], lines: DocLine[]) {
  let subtotal = '0.00'
  let vat = '0.00'
  for (const l of lines) {
    const it = items.find((i) => i.code === l.item_code)
    if (!it) continue
    const p = priceLine(it, l)
    subtotal = addMoney(subtotal, p.net)
    vat = addMoney(vat, p.vat)
  }
  return { subtotal, vat, total: addMoney(subtotal, vat) }
}

// --- numbers -----------------------------------------------------------------

export function docNo(d: Device, kind: DocKind, year = d.bootstrap.fin_year): string {
  return `${d.bootstrap.series[kind].prefix}/${year}/${String(d.next[kind]).padStart(5, '0')}`
}

/** Days since the last download; past the limit the phone stops billing. */
export function daysOffline(d: Device, now = new Date()): number {
  return Math.floor((now.getTime() - new Date(d.downloaded_at).getTime()) / 86_400_000)
}

export function nowUtc(now = new Date()): string {
  return now.toISOString().replace(/\.\d{3}Z$/, 'Z')
}

/** Today in Riyadh, whatever the phone's own time zone. */
export function riyadhDate(now = new Date()): string {
  return new Date(now.getTime() + 3 * 3_600_000).toISOString().slice(0, 10)
}

// --- ZATCA Phase 1 QR ---------------------------------------------------------

function tlv(tag: number, value: string): number[] {
  const bytes = Array.from(new TextEncoder().encode(value))
  if (bytes.length > 255) throw new Error('QR field too long')
  return [tag, bytes.length, ...bytes]
}

/** The five-field TLV, base64 -- byte for byte what the server builds. */
export function zatcaTlv(seller: string, vat: string, issuedAt: string, total: string,
                         vatAmount: string): string {
  const bytes = [
    ...tlv(1, seller.trim()), ...tlv(2, vat.trim()), ...tlv(3, issuedAt),
    ...tlv(4, Number(total).toFixed(2)), ...tlv(5, Number(vatAmount).toFixed(2)),
  ]
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin)
}

export async function qrDataUrl(d: Device, doc: VanDoc): Promise<string | null> {
  const co = d.bootstrap.company
  if (doc.kind !== 'SALE' || !co.vat_number) return null
  const payload = zatcaTlv(co.legal_name || co.name_en, co.vat_number, doc.issued_at,
                           doc.total ?? '0', doc.vat_amount ?? '0')
  return QRCode.toDataURL(payload, { errorCorrectionLevel: 'M', margin: 1, width: 256 })
}

// --- the end of the day ------------------------------------------------------

/** The day in figures, from the documents on this phone. Cash to hand
 *  over is cash sales plus collections less cash refunds. */
export function daySummary(d: Device, docs: VanDoc[], date: string) {
  const today = docs.filter((x) => x.doc_date === date)
  const cash = (x: VanDoc) => !!x.paid || !x.customer_code
  const sum = (xs: VanDoc[], f: (x: VanDoc) => string | undefined) =>
    addMoney('0', ...xs.map((x) => f(x) ?? '0'))
  const sales = today.filter((x) => x.kind === 'SALE')
  const returns = today.filter((x) => x.kind === 'RETURN')
  const coll = today.filter((x) => x.kind === 'RECEIPT')
  const cashSales = sum(sales.filter(cash), (x) => x.total)
  const refunds = sum(returns.filter(cash), (x) => x.total)
  const collections = sum(coll, (x) => x.amount)
  return {
    date, invoices: sales.length, sales: sum(sales, (x) => x.total), cashSales,
    creditSales: sum(sales.filter((x) => !cash(x)), (x) => x.total),
    returns: sum(returns, (x) => x.total), refunds, collections,
    collectionsCount: coll.length,
    cashToHand: subMoney(addMoney(cashSales, collections), refunds),
    stock: d.bootstrap.items.filter((i) => Number(i.stock) !== 0).map((i) => ({
      code: i.code, name: i.name_en, qty: i.stock, unit: i.base_unit })),
  }
}
